import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

const EMPTY_FORM = { full_name: '', work_email: '', organisation: '', job_title: '', website: '' };
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RequestAccessStep = ({ initialEmail, onBack, onSubmit, busy }) => {
  const headingRef = useRef(null);
  const [form, setForm] = useState({ ...EMPTY_FORM, work_email: initialEmail || '' });
  const [errors, setErrors] = useState({});
  const [complete, setComplete] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  useEffect(() => headingRef.current?.focus(), [complete]);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const nextErrors = {};
    if (!form.full_name.trim()) nextErrors.full_name = 'Name is required.';
    if (!EMAIL_PATTERN.test(form.work_email.trim())) nextErrors.work_email = 'Use a valid work email address.';
    if (!form.organisation.trim()) nextErrors.organisation = 'Organisation is required.';
    setErrors(nextErrors);
    setSubmissionError('');
    if (Object.keys(nextErrors).length) return;
    try {
      await onSubmit(form);
      setComplete(true);
    } catch (error) {
      setSubmissionError(error.response?.status === 429 ? 'This request has reached its temporary limit. Try again later.' : 'We could not send your request. Try again shortly.');
    }
  };

  if (complete) {
    return (
      <div className="enterprise-auth-step enterprise-auth-complete" aria-live="polite">
        <CheckCircle2 size={34}/>
        <h2 ref={headingRef} tabIndex="-1">Request received</h2>
        <p>We’ll review your request and contact you within 2 working days.</p>
        <button type="button" className="enterprise-auth-secondary" onClick={onBack}>Return to sign in</button>
      </div>
    );
  }

  return (
    <div className="enterprise-auth-step enterprise-auth-request">
      <h2 ref={headingRef} tabIndex="-1">Request access</h2>
      <p className="enterprise-auth-intro">Tell us about your organisation. Every request is reviewed before a workspace is created.</p>
      <form onSubmit={submit} noValidate>
        <label htmlFor="request-name">Full name</label>
        <input id="request-name" autoComplete="name" value={form.full_name} onChange={(event) => update('full_name', event.target.value)} aria-invalid={Boolean(errors.full_name)} />
        {errors.full_name && <p className="enterprise-auth-error" role="alert">{errors.full_name}</p>}

        <label htmlFor="request-email">Work email</label>
        <input id="request-email" type="email" autoComplete="email" value={form.work_email} onChange={(event) => update('work_email', event.target.value)} aria-invalid={Boolean(errors.work_email)} />
        {errors.work_email && <p className="enterprise-auth-error" role="alert">{errors.work_email}</p>}

        <label htmlFor="request-organisation">Organisation</label>
        <input id="request-organisation" autoComplete="organization" value={form.organisation} onChange={(event) => update('organisation', event.target.value)} aria-invalid={Boolean(errors.organisation)} />
        {errors.organisation && <p className="enterprise-auth-error" role="alert">{errors.organisation}</p>}

        <label htmlFor="request-job-title">Job title <span className="enterprise-auth-optional">(optional)</span></label>
        <input id="request-job-title" autoComplete="organization-title" value={form.job_title} onChange={(event) => update('job_title', event.target.value)} />

        <div className="enterprise-auth-honeypot" aria-hidden="true"><label htmlFor="request-website">Website</label><input id="request-website" tabIndex="-1" autoComplete="off" value={form.website} onChange={(event) => update('website', event.target.value)} /></div>
        {submissionError && <p className="enterprise-auth-error" role="alert">{submissionError}</p>}
        <button className="enterprise-auth-primary" type="submit" aria-busy={busy}>{busy ? 'Sending request…' : 'Request access'}</button>
      </form>
      <p className="enterprise-auth-privacy">By submitting, you agree that Qflow may use these details to assess and respond to your request. See our <a href="/privacy">privacy notice</a>.</p>
      <div className="enterprise-auth-access-callout">
        <p><strong>Already have an account?</strong> <button type="button" className="enterprise-auth-link-button" onClick={onBack}>Sign in</button></p>
      </div>
      <p className="enterprise-auth-help"><strong>Joining an existing organisation?</strong> Use the secure invitation link sent by your administrator.</p>
    </div>
  );
};

export default RequestAccessStep;
